import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Stack,
    FormControl,
    FormLabel,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
} from '@chakra-ui/react';
import { TextField, Grid, MenuItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingFieldDropdown } from '../../../../components';
import { CircularProgress, Typography } from '@material-ui/core';
import { axios } from '../../../../services/axios';
import { NonNegativeInteger } from '../../../../components/maintenance/NumberFormatCustom';
import { classNames } from 'utils/classNames';
import { Button as RBButton, Container, Form, Modal } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { TabContext, TabPanel } from '@material-ui/lab';
import { getInputDarkStyle } from '@/constants';
import { Button, Heading, Input, Text } from '@/components/v4';
import FieldToolTip from '@/components/forms/new/FieldToolTip';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
    textBoxStyling,
    breadCrumbStyling,
    headingStyling,
} from '@/components/forms/new/formStyles';
import { chakraSelectDarkMode, defaultChakraSelectStyle } from '@/constants';
import { Select as ChakraSelect } from 'chakra-react-select';
import useGetCompanyTrunkTypes from './queries/useGetCompanyTrunkTypes';
import useSRTTrunkTypes from 'hooks/useSRTTrunkTypes';
import useGetCompanyServices from '../queries/useGetCompanyServices';
import useGetSbcaasTrunkService from './queries/useGetSbcaasTrunkService';
import TrunkServiceChoice from './TrunkServiceChoice';
import { resolveTrunkAddStep, isModernChoice } from './trunkAddStep';
import { SBC, SRT } from 'pages/dashboards/v5/constants';

// external id
const trunkTypeCondition = 'f4de8d22-bf78-4c05-a677-4692dfc5444f';

// Basic form when you Add for trunks in SBCaaS tile
const AddTrunk = () => {
    const navigate = useNavigate();
    const [trunkCapacity, setTrunkCapacity] = useState(0);
    const [nCapacity, setNCapacity] = useState(0);

    // state access
    const [modalState, setModalState] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [tabView, setTabView] = useState(0);
    const [isHoveredType, setIsHoveredType] = useState(false);
    const [isHoveredName, setIsHoveredName] = useState(false);
    const [isHoveredChannels, setIsHoveredChannels] = useState(false);
    const [availableRegions, setAvailableRegions] = useState();
    const [maxChannels, setMaxChannels] = useState(0);
    // Which SBCaaS variant the trunk is being built against, once the company
    // runs both and the user has picked one.
    const [serviceChoice, setServiceChoice] = useState(null);
    const dispatch = useDispatch();

    // access to the current company selected
    const { currentCompany: currCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { darkMode } = useSelector((state) => state.settings);
    const inputDarkStyle = getInputDarkStyle(darkMode);

    // state access
    const { stateError } = useSelector((state) => {
        return {
            ...state.modal,
        };
    });

    const chakraSelectDark = chakraSelectDarkMode(darkMode);
    const chakraStyles = {
        ...defaultChakraSelectStyle,
        ...chakraSelectDark,
    };

    // Every trunk type creatable on this company, across all platforms.
    const { data: trunkTypes = [] } = useGetCompanyTrunkTypes({
        companyID: currCompany,
    });

    // Which SBCaaS variants this company runs. With only one there is nothing
    // to choose between, so that step is skipped entirely.
    const { data: companyServices, isLoading: servicesLoading } =
        useGetCompanyServices({ companyID: currCompany });
    const hasClassicService = (companyServices || []).some(
        ({ serviceType }) => serviceType === SBC,
    );
    const hasModernService = (companyServices || []).some(
        ({ serviceType }) => serviceType === SRT,
    );
    const needsServiceChoice = hasClassicService && hasModernService;

    // SRT has its own add form, matched by trunk type id — the display name is
    // backend-owned and must not be relied on for routing.
    const { isSRTTrunkType } = useSRTTrunkTypes({ enabled: hasModernService });

    // The modern service's types are reached through its own form, so this page
    // only ever offers the classic ones.
    const classicTrunkTypes = useMemo(
        () => (trunkTypes || []).filter(({ id }) => !isSRTTrunkType(id)),
        [trunkTypes, isSRTTrunkType],
    );

    const step = resolveTrunkAddStep({
        servicesLoaded: !servicesLoading && Boolean(companyServices),
        hasClassicService,
        hasModernService,
        serviceChoice,
        trunkTypeID: modalState?.trunkTypeID,
        classicTrunkTypeCount: classicTrunkTypes.length,
    });

    useEffect(() => {
        if (step === 'redirect-modern') {
            navigate('/trunks/add/srt', { replace: true });
        }
    }, [step, navigate]);

    // Reaching the form with no type settled means a single type was the only
    // option — record it, so the form shows it and submits against it.
    useEffect(() => {
        if (step !== 'form' || modalState?.trunkTypeID) return;
        setState({
            trunkTypeID: classicTrunkTypes[0].id,
            channels: 0,
            regionID: '',
            redundancyLevel: '',
            name: '',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, modalState?.trunkTypeID, classicTrunkTypes]);

    const handleServiceChoice = (serviceType) => {
        if (isModernChoice(serviceType)) {
            navigate('/trunks/add/srt');
            return;
        }
        setServiceChoice(SBC);
    };

    // static enum for the value and label of the redundancy level drop down
    const redundancy = [
        { value: 1, label: 1 },
        { value: 2, label: 2 },
        { value: 3, label: 3 },
        { value: 4, label: 4 },
    ];

    const handleBackbutton = () => {
        dispatch({
            type: 'CHANGE_TABLE_LOADING',
        });
        navigate('/trunks');
    };

    const handleTabChange = (e, value) => {
        setTabView(value);
    };

    // function to set state with a given payload
    const setState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    /*
     * changes n-capacity and trunk capacity whenevnery the redundancy level or channel value changes
     * capacity is redundancy level multiplied by channel value
     * n-capacity redundancy level -1 multipled by channel value
     */
    useEffect(() => {
        if (modalState?.redundancyLevel && modalState?.channels) {
            setTrunkCapacity(modalState.redundancyLevel * modalState.channels);
            setNCapacity(
                (modalState.redundancyLevel - 1) * modalState.channels,
            );
        }
    }, [modalState?.redundancyLevel, modalState?.channels]);

    // Regions and the channel ceiling the classic service's trunks are bound by
    useGetSbcaasTrunkService({
        companyID: currCompany,
        onSuccess: ({ regions, maxChannel }) => {
            setAvailableRegions(regions);
            setMaxChannels(maxChannel);
        },
    });

    /**
     * Validate inputs
     * @returns
     */
    const validateModal = () => {
        const newErrorState = {
            name:
                modalState?.name &&
                !RegExp('^[a-zA-Z0-9- ]+$').test(modalState?.name) &&
                'Must be alphanumeric characters',
            channels:
                (parseInt(modalState?.channels) > maxChannels &&
                    `Max channels limit is ${maxChannels}`) ||
                (modalState?.channels <= 0 &&
                    'Channels must be positive and greater than zero'),
            regionID:
                !modalState?.regionID &&
                modalState?.trunkTypeID !==
                    'f4de8d22-bf78-4c05-a677-4692dfc5444f' &&
                'Region must be selected',
            redundancyLevel:
                !modalState?.redundancyLevel &&
                modalState?.trunkTypeID !==
                    'f4de8d22-bf78-4c05-a677-4692dfc5444f' &&
                'Redundancy level is required',
        };
        dispatch({
            type: 'CHANGE_MODAL_STATE_ERROR',
            payload: newErrorState,
        });
        return !(
            newErrorState.name ||
            newErrorState.channels ||
            newErrorState.trunkTypeID ||
            newErrorState.regionID ||
            newErrorState.redundancyLevel
        );
    };

    async function generatePassword(id) {
        return await axios.get(`/Trunk/${id}/NewSipPassword`);
    }

    /*
     * upload handler for when a trunk is added
     * data is transfomred, if redundancy level isnt listed it defaults to 2
     * on success of the creation, redirect to the edit page
     */
    const submitTrunk = () => {
        setIsLoading(true);
        if (!validateModal()) {
            setIsLoading(false);
            return;
        }
        let redundancy;
        if (!modalState.redundancyLevel) {
            redundancy = 2;
        } else {
            redundancy = modalState.redundancyLevel;
        }
        axios
            .post(
                '/trunk/create',
                {
                    trunkTypeID: modalState?.trunkTypeID,
                    companyID: currCompany,
                    regionID: modalState?.regionID,
                    redundancyLevel: redundancy,
                    channels: modalState?.channels,
                    name: modalState?.name,
                    authType: '1',
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            )
            .then(async (res) => {
                if (res.status === 200) {
                    // If it's an external trunk, we don't need to redirect to the edit screen
                    const externalTrunkId =
                        'f4de8d22-bf78-4c05-a677-4692dfc5444f';
                    if (res.data.trunkTypeID === externalTrunkId) {
                        navigate('/trunks');
                        dispatch({
                            type: 'RESET_TABLE',
                        });
                        toast.success(`${res.data.name} created successfully!`);
                        return;
                    }
                    // for sip registration by default
                    var result = await generatePassword(res.data.id);
                    navigate(`/trunks/edit/sip/${res.data.id}`, {
                        state: result.data,
                    });
                } else {
                    toast.error(res.data);
                    setIsLoading(false);
                }
            })
            .catch((e) => {
                toast.error(e.response.data.message);
                setIsLoading(false);
            });
    };

    const breadCrumbTrail = [
        { label: 'Trunks', path: '/trunks' },
        { label: 'Add Trunk', path: '' },
    ];

    // Check if this is external trunk (Carrier External)
    const isExternalTrunk = modalState?.trunkTypeID === trunkTypeCondition;

    // Render FormBase style for external trunk
    if (isExternalTrunk) {
        return (
            <>
                <Breadcrumb sx={breadCrumbStyling}>
                    {breadCrumbTrail?.map((crumb, index) => (
                        <BreadcrumbItem key={index}>
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path}
                                onClick={
                                    crumb.path === '/trunks'
                                        ? () =>
                                              dispatch({
                                                  type: 'CHANGE_TABLE_LOADING',
                                              })
                                        : undefined
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                    ))}
                </Breadcrumb>

                <Heading sx={headingStyling}>
                    Add Trunk - Carrier External
                </Heading>

                <Box
                    as="form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        submitTrunk();
                    }}>
                    <Box
                        borderRadius={15}
                        padding="30px"
                        maxWidth={895}
                        ml={0}
                        bg={darkMode ? 'dark.tcap' : 'white'}
                        border="1px solid"
                        borderColor={
                            darkMode ? 'dark.tcap-borderGray' : 'gray.200'
                        }>
                        <FormControl
                            sx={{ margin: '0 !important', mb: 4 }}
                            onMouseEnter={() => setIsHoveredType(true)}
                            onMouseLeave={() => setIsHoveredType(false)}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling()}>
                                <FormLabel
                                    sx={formLabelStyling(darkMode, true)}>
                                    Trunk Type
                                </FormLabel>
                                <Box flex={1} maxWidth="400px">
                                    <ChakraSelect
                                        placeholder="- Select Trunk Type -"
                                        value={
                                            classicTrunkTypes.find(
                                                (opt) =>
                                                    opt.id ===
                                                    modalState?.trunkTypeID,
                                            )
                                                ? {
                                                      value: modalState.trunkTypeID,
                                                      label: classicTrunkTypes.find(
                                                          (opt) =>
                                                              opt.id ===
                                                              modalState.trunkTypeID,
                                                      ).name,
                                                  }
                                                : null
                                        }
                                        onChange={(item) => {
                                            setState({
                                                trunkTypeID: item?.value,
                                                channels: 0,
                                                regionID: '',
                                                redundancyLevel: '',
                                                name: '',
                                            });
                                        }}
                                        options={classicTrunkTypes.map(
                                            (type) => ({
                                                value: type.id,
                                                label: type.name,
                                            }),
                                        )}
                                        chakraStyles={chakraStyles}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                        }}
                                    />
                                </Box>
                                {isHoveredType && (
                                    <FieldToolTip
                                        label="Select the type of trunk to create"
                                        ariaLabel="trunk-type-tip"
                                        left="25px"
                                        top="25px"
                                    />
                                )}
                            </Stack>
                        </FormControl>

                        <FormControl
                            sx={{ margin: '0 !important', mb: 4 }}
                            onMouseEnter={() => setIsHoveredName(true)}
                            onMouseLeave={() => setIsHoveredName(false)}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling()}>
                                <FormLabel
                                    sx={formLabelStyling(darkMode, true)}>
                                    Trunk Name
                                </FormLabel>
                                <Box flex={1} maxWidth="400px">
                                    <Input
                                        type="text"
                                        id="trunkName"
                                        name="trunkName"
                                        value={modalState?.name || ''}
                                        onChange={(e) =>
                                            setState({ name: e.target.value })
                                        }
                                        sx={textBoxStyling}
                                        placeholder="Enter trunk name"
                                    />
                                    {stateError?.name && (
                                        <Text
                                            color="red.500"
                                            fontSize="xs"
                                            mt={1}>
                                            {stateError.name}
                                        </Text>
                                    )}
                                </Box>
                                {isHoveredName && (
                                    <FieldToolTip
                                        label="The display name for this trunk"
                                        ariaLabel="trunk-name-tip"
                                        left="25px"
                                        top="5px"
                                    />
                                )}
                            </Stack>
                        </FormControl>

                        <FormControl
                            sx={{ margin: '0 !important', mb: 4 }}
                            onMouseEnter={() => setIsHoveredChannels(true)}
                            onMouseLeave={() => setIsHoveredChannels(false)}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling()}>
                                <FormLabel
                                    sx={formLabelStyling(darkMode, true)}>
                                    Channels
                                </FormLabel>
                                <Box flex={1} maxWidth="400px">
                                    <Input
                                        type="text"
                                        id="channels"
                                        name="channels"
                                        value={modalState?.channels || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (
                                                value === '' ||
                                                /^\d+$/.test(value)
                                            ) {
                                                setState({ channels: value });
                                            }
                                        }}
                                        sx={textBoxStyling}
                                        placeholder="Enter number of channels"
                                    />
                                    {stateError?.channels && (
                                        <Text
                                            color="red.500"
                                            fontSize="xs"
                                            mt={1}>
                                            {stateError.channels}
                                        </Text>
                                    )}
                                </Box>
                                {isHoveredChannels && (
                                    <FieldToolTip
                                        label="Number of channels for this trunk"
                                        ariaLabel="channels-tip"
                                        left="25px"
                                        top="5px"
                                    />
                                )}
                            </Stack>
                        </FormControl>
                    </Box>

                    <Box sx={{ marginTop: '25px', display: 'flex', gap: 2 }}>
                        <Button
                            variant="outline"
                            sx={{ background: 'white', minWidth: '120px' }}
                            onClick={handleBackbutton}>
                            Back
                        </Button>
                        <Button
                            type="submit"
                            sx={{ minWidth: '120px' }}
                            isLoading={isLoading}>
                            Create
                        </Button>
                    </Box>
                </Box>
            </>
        );
    }

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                crumb.path === '/trunks'
                                    ? () =>
                                          dispatch({
                                              type: 'CHANGE_TABLE_LOADING',
                                          })
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>

            {/* <Heading sx={headingStyling}>
                Add Trunk
            </Heading> */}

            {step === 'service-choice' ? (
                <TrunkServiceChoice
                    onSelect={handleServiceChoice}
                    onBack={handleBackbutton}
                />
            ) : step !== 'form' ? (
                <Box
                    as="form"
                    onSubmit={(e) => {
                        e.preventDefault();
                    }}>
                    <Box
                        borderRadius={15}
                        padding="30px"
                        maxWidth={895}
                        ml={0}
                        bg={darkMode ? 'dark.tcap' : 'white'}
                        border="1px solid"
                        borderColor={
                            darkMode ? 'dark.tcap-borderGray' : 'gray.200'
                        }>
                        <FormControl
                            sx={{ margin: '0 !important', mb: 4 }}
                            onMouseEnter={() => setIsHoveredType(true)}
                            onMouseLeave={() => setIsHoveredType(false)}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling()}>
                                <FormLabel
                                    sx={formLabelStyling(darkMode, true)}>
                                    Trunk Type
                                </FormLabel>
                                <Box flex={1} maxWidth="400px">
                                    <ChakraSelect
                                        placeholder="- Select Trunk Type -"
                                        value={
                                            classicTrunkTypes.find(
                                                (opt) =>
                                                    opt.id ===
                                                    modalState?.trunkTypeID,
                                            )
                                                ? {
                                                      value: modalState.trunkTypeID,
                                                      label: classicTrunkTypes.find(
                                                          (opt) =>
                                                              opt.id ===
                                                              modalState.trunkTypeID,
                                                      ).name,
                                                  }
                                                : null
                                        }
                                        onChange={(item) => {
                                            setState({
                                                trunkTypeID: item?.value,
                                                channels: 0,
                                                regionID: '',
                                                redundancyLevel: '',
                                                name: '',
                                            });
                                        }}
                                        options={classicTrunkTypes.map(
                                            (type) => ({
                                                value: type.id,
                                                label: type.name,
                                            }),
                                        )}
                                        chakraStyles={chakraStyles}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                        }}
                                    />
                                </Box>
                                {isHoveredType && (
                                    <FieldToolTip
                                        label="Select the type of trunk to create"
                                        ariaLabel="trunk-type-tip"
                                        left="25px"
                                        top="25px"
                                    />
                                )}
                            </Stack>
                        </FormControl>
                    </Box>

                    <Box sx={{ marginTop: '25px', display: 'flex', gap: 2 }}>
                        <Button
                            variant="outline"
                            sx={{ background: 'white', minWidth: '120px' }}
                            onClick={
                                needsServiceChoice
                                    ? () => setServiceChoice(null)
                                    : handleBackbutton
                            }>
                            Back
                        </Button>
                    </Box>
                </Box>
            ) : (
                <>
                    <Modal.Title>Add Trunk</Modal.Title>
                    <TabContext value={tabView}>
                        <Box
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'brand.500',
                                },
                            }}>
                            <Tabs
                                value={tabView}
                                centered
                                textColor="primary"
                                onChange={handleTabChange}>
                                <Tab label="General" key="General" />
                            </Tabs>
                            <TabPanel
                                className="MuiTypography-body1"
                                value={tabView}>
                                <Container fluid>
                                    <Form
                                        style={{
                                            paddingTop: '1%',
                                            paddingBottom: '1%',
                                        }}
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            submitTrunk();
                                        }}>
                                        {isLoading ? (
                                            <CircularProgress
                                                style={{
                                                    marginTop: '2.5%',
                                                    marginLeft: '50%',
                                                }}
                                            />
                                        ) : (
                                            <>
                                                {/* Grid with the fields required to create or edit a trunk */}
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    className={classNames(
                                                        modalState?.trunkTypeID &&
                                                            modalState?.trunkTypeID !==
                                                                trunkTypeCondition
                                                            ? 'justify-start'
                                                            : 'justify-center',
                                                    )}>
                                                    <Grid item xs={6}>
                                                        <LoadingFieldDropdown
                                                            // The modern
                                                            // service's type is
                                                            // reached through
                                                            // its own form, so
                                                            // it must not be
                                                            // selectable here.
                                                            staticEnum={
                                                                classicTrunkTypes
                                                            }
                                                            init={true}
                                                            fieldName="Trunk Type"
                                                            dropDownValue="id"
                                                            displayField="name"
                                                            noEmptyOption
                                                            // empty space rather than showing not allocated
                                                            noneFieldDisplay=" "
                                                            errorText={
                                                                stateError?.trunkTypeID
                                                            }
                                                            onChange={(v) => {
                                                                setState({
                                                                    trunkTypeID:
                                                                        v,
                                                                    channels: 0,
                                                                    regionID:
                                                                        '',
                                                                    redundancyLevel:
                                                                        '',
                                                                    name: '',
                                                                });
                                                            }}
                                                            fieldValue={
                                                                modalState?.trunkTypeID
                                                            }
                                                        />
                                                    </Grid>

                                                    {modalState?.trunkTypeID && (
                                                        <>
                                                            {/* Regions */}
                                                            {modalState?.trunkTypeID &&
                                                                modalState?.trunkTypeID !==
                                                                    trunkTypeCondition && (
                                                                    <Grid
                                                                        item
                                                                        xs={6}>
                                                                        <LoadingFieldDropdown
                                                                            staticEnum={
                                                                                availableRegions
                                                                            }
                                                                            fieldName="Region"
                                                                            dropDownValue="id"
                                                                            displayField="name"
                                                                            onChange={(
                                                                                v,
                                                                            ) => {
                                                                                setState(
                                                                                    {
                                                                                        regionID:
                                                                                            v,
                                                                                    },
                                                                                );
                                                                            }}
                                                                            fieldValue={
                                                                                modalState?.regionID
                                                                            }
                                                                            toggleLimit={
                                                                                6
                                                                            }
                                                                            errorText={
                                                                                stateError?.regionID
                                                                            }
                                                                            error={Boolean(
                                                                                stateError?.regionID,
                                                                            )}
                                                                            helperText={
                                                                                stateError?.regionID
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                )}

                                                            {/* Trunk Name */}
                                                            <Grid
                                                                item
                                                                xs={3}
                                                                sx={
                                                                    inputDarkStyle
                                                                }>
                                                                <TextField
                                                                    sx={{
                                                                        paddingTop: 0.6,
                                                                    }}
                                                                    fullWidth
                                                                    id="TrunkName"
                                                                    InputLabelProps={{
                                                                        shrink: true,
                                                                    }}
                                                                    label="Trunk Name"
                                                                    size="small"
                                                                    value={
                                                                        modalState?.name
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        setState(
                                                                            {
                                                                                name: e
                                                                                    .target
                                                                                    .value,
                                                                            },
                                                                        );
                                                                    }}
                                                                    error={Boolean(
                                                                        stateError?.name,
                                                                    )}
                                                                    helperText={
                                                                        stateError?.name
                                                                    }
                                                                />
                                                            </Grid>

                                                            {/* Channels */}
                                                            <Grid
                                                                item
                                                                xs={3}
                                                                sx={
                                                                    inputDarkStyle
                                                                }>
                                                                <TextField
                                                                    sx={{
                                                                        paddingTop: 0.6,
                                                                    }}
                                                                    fullWidth
                                                                    id="Channels"
                                                                    InputLabelProps={{
                                                                        shrink: true,
                                                                    }}
                                                                    label="Channels"
                                                                    size="small"
                                                                    value={
                                                                        modalState?.channels
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        setState(
                                                                            {
                                                                                channels:
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                            },
                                                                        );
                                                                    }}
                                                                    InputProps={{
                                                                        inputComponent:
                                                                            NonNegativeInteger,
                                                                    }}
                                                                    error={Boolean(
                                                                        stateError?.channels,
                                                                    )}
                                                                    helperText={
                                                                        stateError?.channels
                                                                    }
                                                                />
                                                            </Grid>
                                                            {/* Redundancy Level */}
                                                            {modalState?.trunkTypeID &&
                                                                modalState?.trunkTypeID !==
                                                                    trunkTypeCondition &&
                                                                modalState?.regionID && (
                                                                    <Grid
                                                                        item
                                                                        xs={3}
                                                                        sx={{
                                                                            ...inputDarkStyle,
                                                                            'div.MuiInputBase-input.MuiOutlinedInput-input':
                                                                                {
                                                                                    color: darkMode
                                                                                        ? '#FFFFFF'
                                                                                        : null,
                                                                                },
                                                                        }}>
                                                                        <TextField
                                                                            sx={{
                                                                                paddingTop: 0.6,
                                                                            }}
                                                                            fullWidth
                                                                            id="Redundancy"
                                                                            InputLabelProps={{
                                                                                shrink: true,
                                                                            }}
                                                                            select
                                                                            label="Redundancy Level"
                                                                            size="small"
                                                                            value={
                                                                                modalState?.redundancyLevel
                                                                            }
                                                                            onChange={(
                                                                                e,
                                                                            ) => {
                                                                                setState(
                                                                                    {
                                                                                        redundancyLevel:
                                                                                            e
                                                                                                .target
                                                                                                .value,
                                                                                    },
                                                                                );
                                                                            }}
                                                                            error={Boolean(
                                                                                stateError?.redundancyLevel,
                                                                            )}
                                                                            helperText={
                                                                                stateError?.redundancyLevel
                                                                            }>
                                                                            {redundancy.map(
                                                                                (
                                                                                    option,
                                                                                ) => (
                                                                                    <MenuItem
                                                                                        key={
                                                                                            option.label
                                                                                        }
                                                                                        value={
                                                                                            option.value
                                                                                        }>
                                                                                        {
                                                                                            option.label
                                                                                        }
                                                                                    </MenuItem>
                                                                                ),
                                                                            )}
                                                                        </TextField>
                                                                    </Grid>
                                                                )}
                                                            {/* Grid with the display of the capacitiy and n-1 capacity that is updated based on state */}
                                                            {modalState?.trunkTypeID &&
                                                                modalState?.trunkTypeID !==
                                                                    trunkTypeCondition && (
                                                                    <Grid
                                                                        item
                                                                        xs={3}>
                                                                        <Box
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                            }}>
                                                                            <Typography>
                                                                                <Box>
                                                                                    Total
                                                                                    Trunk
                                                                                    Capacity:{' '}
                                                                                    {
                                                                                        trunkCapacity
                                                                                    }
                                                                                </Box>
                                                                                <Box
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        justifyContent:
                                                                                            'flex-end',
                                                                                    }}>
                                                                                    n-1
                                                                                    Capacity:{' '}
                                                                                    {
                                                                                        nCapacity
                                                                                    }
                                                                                </Box>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                )}
                                                        </>
                                                    )}
                                                </Grid>

                                                {/* Back/Create Button */}
                                                <div
                                                    style={{
                                                        border: 'none',
                                                        textAlign: 'center',
                                                        justifyContent:
                                                            'center',
                                                        marginTop: 10,
                                                        marginBottom: 10,
                                                    }}>
                                                    <RBButton
                                                        variant="secondary"
                                                        onClick={
                                                            handleBackbutton
                                                        }>
                                                        Back
                                                    </RBButton>
                                                    <div
                                                        style={{
                                                            marginLeft: 10,
                                                            display: 'inline',
                                                        }}>
                                                        <RBButton
                                                            variant="primary"
                                                            type="submit">
                                                            Create
                                                        </RBButton>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </Form>
                                </Container>
                            </TabPanel>
                        </Box>
                    </TabContext>
                </>
            )}
        </>
    );
};

export default AddTrunk;
